import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/apiService';
import BackgroundShapes from '../common/BackgroundShapes';

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();

  // Read raw token from URL query string — e.g. /reset-password?token=<rawToken>
  const token = new URLSearchParams(window.location.search).get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Reset link is invalid or missing. Please request a new one.');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword(token, newPassword);
      // Navigate to login with a success indicator in state
      navigate('/login', { state: { passwordReset: true } });
    } catch (err: any) {
      // Surface the backend message (e.g. "Token invalid or expired")
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />

      <div className="container mx-auto px-4 py-12 relative z-10">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-coffee mb-2">Reset Password</h1>
            <p className="text-coffee opacity-80">Enter your new password below</p>
          </div>

          {/* Card */}
          <div className="bg-cream border-2 border-coffee rounded-xl p-8 shadow-md">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-100 border-2 border-red-400 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                  {error.includes('invalid or expired') && (
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={() => navigate('/forgot-password')}
                        className="underline text-red-700 hover:text-red-900 text-sm"
                      >
                        Request a new reset link
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* New Password */}
              <div>
                <label htmlFor="newPassword" className="block text-coffee text-sm font-medium mb-2">
                  New password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      if (error) setError('');
                    }}
                    placeholder="At least 8 characters"
                    className="w-full px-4 py-3 pr-12 rounded-lg bg-white border border-coffee text-coffee placeholder-coffee placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta transition-colors"
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-coffee hover:text-terracotta transition-colors"
                    disabled={loading}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-coffee text-sm font-medium mb-2">
                  Confirm new password
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (error) setError('');
                  }}
                  placeholder="Re-enter your password"
                  className="w-full px-4 py-3 rounded-lg bg-white border border-coffee text-coffee placeholder-coffee placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta transition-colors"
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-terracotta text-white py-3 rounded-lg font-bold text-lg hover:bg-[#d89050] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center border-2 border-coffee shadow-lg"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Resetting...
                  </>
                ) : (
                  'Reset password'
                )}
              </button>
            </form>
          </div>

          {/* Back link */}
          <div className="text-center mt-6">
            <button
              onClick={() => navigate('/login')}
              className="text-coffee hover:text-terracotta text-sm transition-colors"
            >
              ← Back to Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
